// JavaScript
// src/pages/breweries/AddBreweryDialog.jsx
// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../utils/firebase';
import * as Sentry from '@sentry/react';
// material
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
// data
import states from '../../shared/states/usStates.json';

/**
 * AddBreweryDialog renders the modal dialog and manages form state for creating a new brewery.
 * It validates input, calls the backend function to create the brewery, and notifies the parent on success.
 */
function AddBreweryDialog({
  open,
  onClose,
  usState,
  isGlobalAdmin,
  authUser,
  onCreated,
}) {
  const [form, setForm] = useState({
    name: '',
    address1: '',
    city: '',
    state: usState,
    zip: '',
    phone: '',
    website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // When opened or when parent state changes, keep the local state field in sync.
  useEffect(() => {
    if (open) {
      setForm((prev) => ({ ...prev, state: usState }));
      setErrorMsg('');
    }
  }, [open, usState]);

  /**
   * handleFieldChange updates the form state when inputs change.
   */
  const handleFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  /**
   * handleCreateBrewery validates inputs, calls the create function, then notifies parent on success.
   */
  const handleCreateBrewery = useCallback(async () => {
    if (!isGlobalAdmin) {
      setErrorMsg('Only admins can add breweries.');
      return;
    }

    const name = form.name?.trim();
    const address1 = form.address1?.trim();
    const city = form.city?.trim();
    const stateInput = (form.state || usState || '').trim();
    let stateVal = '';
    if (stateInput) {
      const inputUpper = stateInput.toUpperCase();
      const byCode = states.find((s) => s.code.toUpperCase() === inputUpper);
      if (byCode) {
        stateVal = byCode.code;
      } else {
        const byName = states.find(
          (s) => s.name.toUpperCase() === stateInput.toUpperCase(),
        );
        if (byName) {
          stateVal = byName.code;
        }
      }
    }
    if (!stateVal) {
      setErrorMsg('State must be a valid 2-letter US code.');
      return;
    }
    const zip = form.zip?.trim();

    if (!name || !address1 || !city || !stateVal || !zip) {
      setErrorMsg(
        'Please fill all required fields: Name, Address, City, State, ZIP.',
      );
      return;
    }

    if (!authUser?.uid) {
      setErrorMsg('You must be signed in.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      const companyLocationRaw = {
        name,
        address1,
        city,
        state: stateVal,
        zip,
        phone: form.phone?.trim(),
        website: form.website?.trim(),
        type: 'Brewery',
        timestamps: {
          createdDate: Date.now(),
        },
      };

      // Remove empty optional fields but keep required ones
      const companyLocation = Object.fromEntries(
        Object.entries(companyLocationRaw).filter(([key, val]) => {
          if (key === 'type' || key === 'name') return true;
          return Boolean(val);
        }),
      );

      const createBreweryFn = httpsCallable(
        functions,
        'callableCompanyLocationCreateBrewery-createBrewery',
      );
      const res = await createBreweryFn({
        companyLocation,
        userId: authUser.uid,
      });
      const breweryId = res?.data?.breweryId;
      if (breweryId) {
        if (onClose) onClose();
        if (onCreated) onCreated(breweryId);
      } else {
        setErrorMsg('Failed to create brewery. Please try again.');
      }
    } catch (err) {
      Sentry.captureException(err);
      setErrorMsg('Failed to create brewery. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [authUser, form, isGlobalAdmin, onClose, onCreated, usState]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Brewery</DialogTitle>
      <DialogContent>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}
        <Stack spacing={2} sx={{
          mt: 1
        }}>
          <TextField
            label="Name"
            name="name"
            value={form.name}
            onChange={handleFieldChange}
            required
            fullWidth
          />
          <TextField
            label="Address 1"
            name="address1"
            value={form.address1}
            onChange={handleFieldChange}
            required
            fullWidth
          />
          <TextField
            label="City"
            name="city"
            value={form.city}
            onChange={handleFieldChange}
            required
            fullWidth
          />
          <TextField
            label="State"
            name="state"
            value={form.state}
            onChange={handleFieldChange}
            required
            fullWidth
          />
          <TextField
            label="ZIP"
            name="zip"
            value={form.zip}
            onChange={handleFieldChange}
            required
            fullWidth
          />
          <TextField
            label="Phone"
            name="phone"
            value={form.phone}
            onChange={handleFieldChange}
            fullWidth
          />
          <TextField
            label="Website"
            name="website"
            value={form.website}
            onChange={handleFieldChange}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateBrewery}
          disabled={submitting || !isGlobalAdmin || !authUser?.uid}
          variant="contained"
        >
          {submitting ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddBreweryDialog;
